/**
 * Product Grid Component for Category Product Display
 * 
 * A responsive grid layout component that displays products for specific
 * bike categories with integrated configurator card and intelligent
 * product sorting and presentation.
 * 
 * Features:
 * - Responsive two-column grid layout
 * - Automatic product sorting by price (low to high)
 * - Integrated configuration card for customization options
 * - Consistent height management for visual alignment
 * - Category-specific product filtering and display
 * 
 * Product Management:
 * - Dynamic product data from Sanity CMS
 * - Intelligent price parsing and sorting
 * - Product card integration with hover effects
 * - Link generation for product detail pages
 * - Category-specific fallback handling
 * 
 * Sorting Logic:
 * - Price-based sorting for customer convenience
 * - Robust price parsing (handles strings, numbers, currencies)
 * - Fallback handling for missing or invalid price data
 * - Consistent ordering across different data formats
 * 
 * Visual Design:
 * - Clean grid layout with consistent spacing
 * - Professional product card presentation
 * - Integrated configurator card for customization
 * - Responsive design for all screen sizes
 * - Consistent height management for visual harmony
 * 
 * Content Integration:
 * - Product title, pricing, and descriptions
 * - Material and weight specifications
 * - Product images with hover effects
 * - Category-specific product links
 * - Configuration options integration
 * 
 * User Experience:
 * - Logical price-based product ordering
 * - Clear product differentiation and comparison
 * - Easy navigation to product details
 * - Customization options prominently displayed
 * - Touch-friendly mobile interactions
 * 
 * Performance:
 * - Efficient product sorting algorithms
 * - Optimized rendering with stable keys
 * - Component reusability and composition
 * - Minimal re-renders with proper dependencies
 * 
 * Accessibility:
 * - Semantic HTML structure for product listings
 * - Keyboard navigation for product cards
 * - Screen reader compatible product information
 * - Clear visual hierarchy and content organization
 * 
 * @param {Array} products - Array of product objects from Sanity CMS
 * @param {object} configureCard - Configuration card content and settings
 * @param {string} category - Product category for context and routing
 * 
 * @description Responsive product grid with intelligent sorting and configuration integration
 */

import ProductCard from '../products/ProductCard';
import ConfigureCard from '../products/ConfigureCard';

const ProductGrid = ({ 
  products, 
  configureCard = {}, 
  category 
}) => {
  // Sort products by price (low to high)
  const sortedProducts = [...(products || [])].sort((a, b) => {
    // Handle case when price is undefined, null, or not a number
    const getPriceValue = (price) => {
      if (typeof price === 'number') return price;
      
      // If price is a string, remove Euro symbol, spaces, and convert to number
      if (typeof price === 'string') {
        // Replace any non-numeric chars except for decimal point
        const numericString = price.replace(/[^0-9.]/g, '');
        return parseFloat(numericString) || 0;
      }
      
      return 0; // Default for undefined or null
    };
    
    return getPriceValue(a.price) - getPriceValue(b.price);
  });

  return (
    <div className="container mx-auto px-8 sm:px-16 md:px-24 lg:px-32 py-20">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 sm:gap-10">
        {/* Configure card - always displayed first for immediate visibility */}
        <div className="h-full">
          <ConfigureCard 
            title={configureCard.title}
            buttonText={configureCard.buttonText}
            productLink={configureCard.productLink}
            backgroundImage={configureCard.backgroundImage}
          />
        </div>

        {/* Render sorted products after configure card */}
        {sortedProducts.map((product, index) => (
          <div key={`product-container-${product._id || index}`} className="h-full">
            <ProductCard
              key={`product-${product._id || index}`}
              title={product.title}
              price={product.price}
              boldDescription={product.cardShortDescriptionBold}
              greyDescription={product.cardShortDescriptionGrey}
              material={product.material}
              weight={product.weight}
              productLink={`/product/${product.slug?.current || product.slug}`}
              coverImage={product.cardImages && product.cardImages.length > 0 ? product.cardImages[0] : null}
              hoverImage={product.cardImages && product.cardImages.length > 1 ? product.cardImages[1] : null}
              category={category}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProductGrid; 